import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Button, Checkbox, EmptyState, Field, Modal, Notice, PageHeader, RowActions, Select, Table, Tag } from '@voice/ui';
import { ApiError, api } from '../lib/api';
import type { RoleView, UserView, UsersResponse } from '../lib/types';
import styles from './Pages.module.css';

const KEY = ['users'] as const;
const errorText = (err: unknown) => (err instanceof ApiError ? err.message : 'Etwas ist schiefgelaufen.');

export function Users({ currentUserId }: { currentUserId: string }) {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserView | null>(null);
  const query = useQuery({ queryKey: KEY, queryFn: () => api<UsersResponse>('/users') });

  return (
    <div className={styles.page}>
      <PageHeader title="Benutzer">
        <Button onClick={() => setCreating(true)}>Neuer Benutzer</Button>
      </PageHeader>
      {query.isPending && <p className={styles.note}>Lädt …</p>}
      {query.isError && <Notice tone="error">Die Benutzer konnten nicht geladen werden.</Notice>}
      {query.data && query.data.users.length === 0 && <EmptyState>Keine Benutzer.</EmptyState>}
      {query.data && query.data.users.length > 0 && (
        <Table aria-label="Benutzer">
          <thead>
            <tr>
              <th>Name</th>
              <th>E-Mail</th>
              <th>Rolle</th>
              <th>Letzte Anmeldung</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {query.data.users.map((u) => (
              <tr key={u.id}>
                <th scope="row">
                  {u.name} {u.id === currentUserId && <Tag>Du</Tag>} {!u.active && <Tag tone="busy">Deaktiviert</Tag>}
                </th>
                <td>{u.email}</td>
                <td>{u.roleLabel}</td>
                <td className={styles.muted}>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('de-DE') : 'noch nie'}</td>
                <td>
                  <RowActions>
                    <Button variant="secondary" onClick={() => setEditing(u)}>
                      Bearbeiten
                    </Button>
                  </RowActions>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {creating && query.data && <CreateUser roles={query.data.roles} onClose={() => setCreating(false)} />}
      {editing && query.data && <EditUser user={editing} roles={query.data.roles} isSelf={editing.id === currentUserId} onClose={() => setEditing(null)} />}
    </div>
  );
}

function RoleSelect({ roles, value, onChange, disabled }: { roles: RoleView[]; value: string; onChange: (v: string) => void; disabled?: boolean }) {
  return (
    <Select label="Rolle" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      {roles.map((r) => (
        <option key={r.id} value={r.name}>
          {r.label}
        </option>
      ))}
    </Select>
  );
}

function CreateUser({ roles, onClose }: { roles: RoleView[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [roleName, setRoleName] = useState(roles.find((r) => r.name === 'agent')?.name ?? roles[0]?.name ?? '');
  const create = useMutation({
    mutationFn: () => api('/users', { method: 'POST', body: { email, name, password, roleName } }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY });
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <Modal open onClose={onClose} title="Neuer Benutzer" size="sm">
      <form className={styles.form} onSubmit={submit}>
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        <Field label="E-Mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <Field label="Passwort" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={12} required />
        <p className={styles.muted}>Mindestens 12 Zeichen.</p>
        <RoleSelect roles={roles} value={roleName} onChange={setRoleName} />
        {create.isError && <Notice tone="error">{errorText(create.error)}</Notice>}
        <div className={styles.inline} style={{ justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={create.isPending || !email || !name || password.length < 12}>
            Anlegen
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function EditUser({ user, roles, isSelf, onClose }: { user: UserView; roles: RoleView[]; isSelf: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(user.name);
  const [roleName, setRoleName] = useState(user.roleName);
  const [active, setActive] = useState(user.active);
  const [password, setPassword] = useState('');
  const save = useMutation({
    mutationFn: () => api('/users/' + user.id, { method: 'PATCH', body: { name, ...(isSelf ? {} : { roleName, active }), ...(password ? { password } : {}) } }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY });
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api('/users/' + user.id, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY });
      onClose();
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={user.name}
      description={user.email}
      size="sm"
      footer={
        <>
          {!isSelf && (
            <Button variant="secondary" onClick={() => { if (confirm(`Benutzer „${user.name}“ löschen?`)) remove.mutate(); }} disabled={remove.isPending}>
              Löschen
            </Button>
          )}
          <Button onClick={() => save.mutate()} disabled={save.isPending || (password.length > 0 && password.length < 12)}>
            Speichern
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <RoleSelect roles={roles} value={roleName} onChange={setRoleName} disabled={isSelf} />
        <Checkbox label="Konto aktiv" checked={active} onChange={(e) => setActive(e.target.checked)} disabled={isSelf} hint={isSelf ? 'Das eigene Konto kann nicht deaktiviert werden.' : 'Deaktivierte Benutzer werden sofort abgemeldet.'} />
        <Field label="Neues Passwort (optional)" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="mind. 12 Zeichen" />
        {(save.isError || remove.isError) && <Notice tone="error">{errorText(save.error ?? remove.error)}</Notice>}
      </div>
    </Modal>
  );
}
